import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Mention } from '@tiptap/extension-mention';
import { common, createLowlight } from 'lowlight';
import type { JSONContent } from '@tiptap/react';
import { EditorToolbar } from './EditorToolbar';
import { usePreferences } from '../../contexts/PreferencesContext';
import { createMentionSuggestion } from './MentionExtension';
import { tagSuggestion } from './TagExtension';
import { entityLinkSuggestion } from './EntityLinkExtension';
import { TaskReference } from './TaskReferenceExtension';
import { NoteReference } from './NoteReferenceExtension';
import { EntityLinkNode } from './EntityLinkNode';
import { ImageUpload } from './ImageUploadExtension';
import { createFollowUpExtension } from './FollowUpExtension';
import { AutoSection } from './AutoSectionExtension';
import { Indent } from './IndentExtension';
import Details, { DetailsContent, DetailsSummary } from '@tiptap/extension-details';
import './editor-styles.css';

const lowlight = createLowlight(common);

/**
 * Extended Mention node that adds `color` and `followUpTaskId` attributes.
 * The stock Mention extension only has id, label, and mentionSuggestionChar.
 */
const DossierMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-color'),
        renderHTML: (attributes) => {
          if (!attributes.color) return {};
          return { 'data-color': attributes.color };
        },
      },
      followUpTaskId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-follow-up-task-id'),
        renderHTML: (attributes) => {
          if (!attributes.followUpTaskId) return {};
          return { 'data-follow-up-task-id': attributes.followUpTaskId };
        },
      },
    };
  },
});

interface DossierEditorProps {
  content: JSONContent | null;
  onUpdate: (content: JSONContent) => void;
  editable?: boolean;
  placeholder?: string;
  meetingInstanceId?: string;
  onTaskCreated?: () => void;
  onNoteCreated?: () => void;
  onTaskStatusChange?: (taskId: string, newStatus: string) => void;
  editorRef?: (editor: Editor | null) => void;
  onMentionInserted?: (personId: string, personName: string) => void;
  onFollowUpUndo?: (taskId: string) => void;
  onFollowUpTextChanged?: (taskId: string, newText: string) => void;
}

export function DossierEditor({
  content,
  onUpdate,
  editable = true,
  placeholder = 'Start writing...',
  meetingInstanceId,
  onTaskCreated,
  onNoteCreated,
  onTaskStatusChange,
  editorRef,
  onMentionInserted,
  onFollowUpUndo,
  onFollowUpTextChanged,
}: DossierEditorProps) {
  const { prefs } = usePreferences();
  const [toolbarVisible, setToolbarVisible] = useState(prefs['editor.toolbarVisible'] !== 'false');

  // Use refs to keep callbacks stable for extensions
  const onMentionInsertedRef = useRef(onMentionInserted);
  useEffect(() => { onMentionInsertedRef.current = onMentionInserted; }, [onMentionInserted]);

  const onFollowUpUndoRef = useRef(onFollowUpUndo);
  useEffect(() => { onFollowUpUndoRef.current = onFollowUpUndo; }, [onFollowUpUndo]);

  const onFollowUpTextChangedRef = useRef(onFollowUpTextChanged);
  useEffect(() => { onFollowUpTextChangedRef.current = onFollowUpTextChanged; }, [onFollowUpTextChanged]);

  // Create stable mention suggestion that reads from ref
  const mentionSuggestion = useMemo(() => createMentionSuggestion({
    onMentionInserted: (personId, personName) => {
      onMentionInsertedRef.current?.(personId, personName);
    },
  }), []);

  // Create follow-up extension (stable)
  const followUpExtension = useMemo(() => createFollowUpExtension({
    onUndo: (taskId) => {
      onFollowUpUndoRef.current?.(taskId);
    },
    onTextChanged: (taskId, newText) => {
      onFollowUpTextChangedRef.current?.(taskId, newText);
    },
  }), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable the built-in codeBlock so we use lowlight version
        codeBlock: false,
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Highlight.configure({
        multicolor: false,
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      DossierMention.configure({
        HTMLAttributes: {
          class: 'mention-node',
        },
        renderHTML({ node }) {
          const char = node.attrs.mentionSuggestionChar || '@';
          const label = node.attrs.label ?? node.attrs.id;
          const isTag = char === '#';

          const attrs: Record<string, string> = {
            'data-type': 'mention',
            'data-id': node.attrs.id,
            'data-label': label,
            'data-mention-suggestion-char': char,
            class: isTag ? 'mention-node mention-tag' : 'mention-node mention-person',
          };

          // For tags, store the color as a data attribute and inline style
          if (isTag && node.attrs.color) {
            attrs['data-color'] = node.attrs.color;
            attrs.style = `--tag-color: ${node.attrs.color}`;
          }

          // For follow-up mentions, store the task id
          if (node.attrs.followUpTaskId) {
            attrs['data-follow-up-task-id'] = node.attrs.followUpTaskId;
          }

          return ['span', attrs, `${char}${label}`];
        },
        renderText({ node }) {
          const char = node.attrs.mentionSuggestionChar || '@';
          return `${char}${node.attrs.label ?? node.attrs.id}`;
        },
        deleteTriggerWithBackspace: true,
        suggestions: [mentionSuggestion, tagSuggestion],
      }),
      TaskReference.configure({
        onTaskStatusChange,
      }),
      NoteReference,
      EntityLinkNode.configure({
        suggestion: entityLinkSuggestion,
      }),
      ImageUpload,
      Details.configure({
        persist: true,
        HTMLAttributes: {
          class: 'dossier-details',
        },
      }),
      DetailsContent,
      DetailsSummary,
      AutoSection,
      Indent,
      followUpExtension,
    ],
    content: content ?? undefined,
    editable,
    onUpdate: ({ editor: ed }) => {
      onUpdate(ed.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
    },
  });

  // Expose editor instance to parent
  useEffect(() => {
    editorRef?.(editor);
    return () => editorRef?.(null);
  }, [editor, editorRef]);

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-bg-card">
      {editable && toolbarVisible && (
        <EditorToolbar
          editor={editor}
          meetingInstanceId={meetingInstanceId}
          onTaskCreated={onTaskCreated}
          onNoteCreated={onNoteCreated}
        />
      )}
      {editable && !toolbarVisible && (
        <button
          onClick={() => setToolbarVisible(true)}
          className="w-full px-2 py-0.5 text-[10px] text-text-muted hover:text-text-secondary hover:bg-bg-hover transition-colors text-left"
          title="Show formatting toolbar"
        >
          Aa
        </button>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
